import { Fragment, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrainStore } from '@/state/brainStore';
import { useSessionStore } from '@/state/sessionStore';
import { useSimulationStore } from '@/state/simulationStore';
import { buildHeatmap, type HeatmapCell } from '@/domain/queueEngine';
import { availabilityCellState, DAY_OFF_MARKER } from '@/components/queue/availabilityCell';
import clsx from 'clsx';
import type { StudioId } from '@/domain/types';

const DAY_LABEL: Record<HeatmapCell['dayOfWeek'], string> = {
  sun: 'Sun',
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
};

// Same traffic-light mapping the queue heatmap uses. Mint = booked solid
// (healthy); gold = partial gap; blood = wide-open gap that needs filling.
function cellClass(freeHours: number, workingHours: number): string {
  if (workingHours === 0) return 'bg-ink-soft/30 text-cream-soft/40';
  if (freeHours === 0) return 'bg-mint/60 text-ink-black';
  if (freeHours <= 2) return 'bg-gold/50 text-ink-black';
  if (freeHours <= 4) return 'bg-gold/65 text-ink-black';
  return 'bg-blood/55 text-cream';
}

export default function ArtistAvailability() {
  const navigate = useNavigate();
  const studios = useBrainStore((s) => s.studios);
  const artists = useBrainStore((s) => s.artists);
  const activeArtists = useMemo(
    () => Object.fromEntries(Object.entries(artists).filter(([, a]) => !a.archived)),
    [artists],
  );
  const bookings = useBrainStore((s) => s.bookings);
  const clockIso = useSimulationStore((s) => s.clockIso);
  const assignedStudios = useSessionStore((s) => s.assignedStudios);

  const studioList = Object.values(studios);
  const [studioFilter, setStudioFilter] = useState<StudioId | 'all'>(
    assignedStudios[0] ?? 'all',
  );

  const studioIds: StudioId[] = useMemo(
    () =>
      studioFilter === 'all' ? (Object.keys(studios) as StudioId[]) : [studioFilter],
    [studioFilter, studios],
  );

  const rows = useMemo(
    () => buildHeatmap({ artists: activeArtists, bookings, nowIso: clockIso, studioIds, days: 14 }),
    [activeArtists, bookings, clockIso, studioIds],
  );

  const grouped = useMemo(() => {
    const map = new Map<string, typeof rows>();
    for (const r of rows) {
      const sid = r.artist.studioId;
      if (!map.has(sid)) map.set(sid, []);
      map.get(sid)!.push(r);
    }
    return [...map.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([studioId, group]) => ({
        studioId,
        studioName: studios[studioId]?.name ?? studioId.replace('-', ' '),
        rows: group,
      }));
  }, [rows, studios]);
  const showStudioHeaders = grouped.length > 1;

  const days = rows[0]?.cells.map((c) => ({
    label: DAY_LABEL[c.dayOfWeek],
    date: new Date(c.dayIso).getDate(),
    dayIso: c.dayIso,
  })) ?? [];

  const openCalendar = (artistId: string, dayIso: string) => {
    const artist = activeArtists[artistId];
    if (!artist) return;
    navigate(`/calendar/${artist.studioId}`, {
      state: { focusArtistId: artistId, focusDayIso: dayIso },
    });
  };

  return (
    <section className="px-8 py-8" data-testid="artist-availability">
      <header className="flex items-end justify-between max-w-7xl flex-wrap gap-4">
        <div>
          <p className="label-small-caps text-gold-bright">Network ops</p>
          <h1 className="heading-display text-3xl text-cream mt-2">Artist availability</h1>
          <p className="font-serif text-cream-soft mt-3 max-w-2xl">
            Two-week glance. Click any cell to jump straight to the calendar for that artist
            on that day. Green is healthy. Red is a gap your team needs to fill.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="font-inter text-[10px] uppercase tracking-widest text-cream-soft">
            Studio
          </label>
          <select
            value={studioFilter}
            onChange={(e) => setStudioFilter(e.target.value as StudioId | 'all')}
            className="bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-1.5 text-sm text-cream"
            data-testid="availability-studio-filter"
          >
            <option value="all">All studios</option>
            {studioList.map((s) => (
              <option key={s.id} value={s.id}>
                {s.brand} · {s.name}
              </option>
            ))}
          </select>
        </div>
      </header>

      <div className="mt-6 panel p-4 max-w-full overflow-x-auto">
        <table className="w-full text-xs border-separate border-spacing-1">
          <thead>
            <tr>
              <th className="text-left px-2 py-1 sticky left-0 bg-ink-deep z-10">
                <span className="font-inter text-[10px] text-cream-soft uppercase tracking-widest">
                  Artist
                </span>
              </th>
              {days.map((d) => (
                <th
                  key={d.dayIso}
                  className="text-center px-1 py-1 min-w-[44px] text-cream-soft"
                >
                  <p className="font-inter text-[9px] uppercase tracking-widest">{d.label}</p>
                  <p className="font-inter text-sm">{d.date}</p>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grouped.map((group) => (
              <Fragment key={`avail-${group.studioId}`}>
                {showStudioHeaders ? (
                  <tr data-testid={`avail-studio-${group.studioId}`}>
                    <td
                      colSpan={days.length + 1}
                      className="pt-3 pb-1 label-small-caps text-gold-bright"
                    >
                      {group.studioName}
                    </td>
                  </tr>
                ) : null}
                {group.rows.map((row) => (
                  <tr key={row.artist.id}>
                    <th className="text-left px-2 py-1 sticky left-0 bg-ink-deep z-10">
                      <p className="font-inter text-sm text-cream">{row.artist.displayName}</p>
                    </th>
                    {row.cells.map((cell) => {
                      const testId = `avail-cell-${cell.artistId}-${cell.dayIso.slice(0, 10)}`;
                      const st = availabilityCellState(row.artist.availability, cell);
                      // Comp days + leave get a distinct marker; routine RDOs stay a
                      // quiet "·" off cell so they don't clutter the two-week glance.
                      if (st.kind === 'comp' || st.kind === 'leave') {
                        const m = DAY_OFF_MARKER[st.kind];
                        return (
                          <td key={cell.dayIso} className="p-0">
                            <div
                              data-testid={testId}
                              title={`${row.artist.displayName} · ${m.label}${st.note ? ` — ${st.note}` : ''}`}
                              className={clsx(
                                'w-full h-9 rounded flex items-center justify-center text-[11px] font-mono border border-transparent',
                                m.cls,
                              )}
                            >
                              {m.letter}
                            </div>
                          </td>
                        );
                      }
                      return (
                        <td key={cell.dayIso} className="p-0">
                          <button
                            type="button"
                            onClick={() => openCalendar(cell.artistId, cell.dayIso)}
                            disabled={st.off}
                            data-testid={testId}
                            title={
                              st.off
                                ? `${row.artist.displayName} off`
                                : `${row.artist.displayName} · ${st.freeHours}/${st.workingHours}h free · click to open calendar`
                            }
                            className={clsx(
                              'w-full h-9 rounded text-center text-[11px] font-mono transition border border-transparent',
                              st.off
                                ? 'bg-ink-soft/30 text-cream-soft/40'
                                : cellClass(st.freeHours, st.workingHours),
                              st.off
                                ? 'cursor-not-allowed opacity-50'
                                : 'cursor-pointer hover:border-gold-bright',
                            )}
                          >
                            {st.off ? '·' : st.freeHours}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 panel p-4 flex flex-wrap gap-6 max-w-3xl">
        <p className="label-small-caps">Legend</p>
        <span className="inline-flex items-center gap-2 text-xs">
          <span className="h-3 w-6 rounded bg-mint/60" /> Booked solid · healthy
        </span>
        <span className="inline-flex items-center gap-2 text-xs">
          <span className="h-3 w-6 rounded bg-gold/55" /> 1–4h gap · fill it
        </span>
        <span className="inline-flex items-center gap-2 text-xs">
          <span className="h-3 w-6 rounded bg-blood/55" /> 5h+ gap · urgent
        </span>
        <span className="inline-flex items-center gap-2 text-xs">
          <span className="h-3 w-6 rounded bg-ink-soft/30 border border-gold-dim/20" /> RDO
        </span>
        <span className="inline-flex items-center gap-2 text-xs">
          <span className="h-3 w-6 rounded bg-azure/45 flex items-center justify-center text-ink-black text-[9px]">C</span> Comp day
        </span>
        <span className="inline-flex items-center gap-2 text-xs">
          <span className="h-3 w-6 rounded bg-gold-dim/55 flex items-center justify-center text-ink-black text-[9px]">L</span> Leave
        </span>
        <span className="font-inter text-[10px] text-cream-faint uppercase tracking-widest">
          Cell number = free hours
        </span>
      </div>
    </section>
  );
}
